import { useState, useEffect } from "react";
import { DateInput } from "@/components/DateInput";
import { fmtDate } from "@/lib/dates";
import { Plus, Receipt, Trash2, Pencil, RefreshCw, History } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/Button";
import { Modal, Field, TextInput } from "@/components/Modal";
import { EmptyState, Loader } from "@/components/common";
import { ContributionHistory } from "@/components/space/ContributionHistory";

const STATUSES = [["upcoming", "Upcoming"], ["overdue", "Overdue"], ["paid", "Paid"]];

export function BillsSection({ cid }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [sortBy, setSortBy] = useState("due_date");
  const [contribFor, setContribFor] = useState(null);
  const [amount, setAmount] = useState("");
  const [historyId, setHistoryId] = useState(null);
  const historyBill = items.find((b) => b.id === historyId);

  const removeContribution = async (c) => {
    if (!confirm(`Remove your $${Number(c.amount).toFixed(2)} contribution?`)) return;
    try { await api.delete(`/connections/${cid}/bills/${historyId}/contributions/${c.id}`); await load(true); toast.success("Contribution removed"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const contribute = async () => {
    if (!amount || Number(amount) <= 0) return toast.error("Enter an amount.");
    setBusy(true);
    try {
      const { data } = await api.post(`/connections/${cid}/bills/${contribFor.id}/contribute`, { amount: Number(amount) });
      setContribFor(null); setAmount(""); await load();
      toast.success(data.status === "paid" ? "Bill fully paid!" : "Contribution added");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try { const { data } = await api.get(`/connections/${cid}/bills`); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  const startAdd = () => { setEditing(null); setForm({ name: "", amount: "", due_date: "", recurring: false, frequency: "monthly", status: "upcoming" }); setOpen(true); };
  const startEdit = (b) => { setEditing(b); setForm({ ...b, amount: b.amount ?? "" }); setOpen(true); };

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Enter a bill name.");
    setBusy(true);
    try {
      const payload = { name: form.name, amount: Number(form.amount || 0), due_date: form.due_date || null, recurring: !!form.recurring, frequency: form.frequency || "monthly", status: form.status || "upcoming" };
      if (editing) await api.put(`/connections/${cid}/bills/${editing.id}`, payload);
      else await api.post(`/connections/${cid}/bills`, payload);
      setOpen(false); await load(); toast.success("Saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const markPaid = async (b) => { await api.put(`/connections/${cid}/bills/${b.id}`, { ...b, status: b.status === "paid" ? "upcoming" : "paid" }); await load(); };
  const del = async (b) => { if (confirm("Delete this bill?")) { await api.delete(`/connections/${cid}/bills/${b.id}`); await load(); } };

  const sorted = [...items].sort((a, b) => {
    if (sortBy === "amount") return b.amount - a.amount;
    if (sortBy === "name") return a.name.localeCompare(b.name);
    return (a.due_date || "").localeCompare(b.due_date || "");
  });

  if (loading) return <Loader />;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="iks-input px-3 py-2 text-sm">
          <option value="due_date" className="bg-slate-900">Due date</option>
          <option value="amount" className="bg-slate-900">Amount</option>
          <option value="name" className="bg-slate-900">Name</option>
        </select>
        <Button onClick={startAdd} data-testid="add-bill-btn"><Plus size={16} /> Add Bill</Button>
      </div>

      {sorted.length === 0 ? (
        <EmptyState icon={Receipt} title="No bills or subscriptions" subtitle="Keep track of shared bills, who pays, and what's due." action={<Button onClick={startAdd}><Plus size={16} /> Add Bill</Button>} testId="bills-empty" />
      ) : (
        <div className="space-y-2">
          {sorted.map((b) => (
            <div key={b.id} className="iks-card p-4 group" data-testid="bill-card">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-100 break-words">{b.name}</p>
                    {b.recurring && <RefreshCw size={12} className="text-sky-400 shrink-0" />}
                  </div>
                  <p className="text-slate-500 text-xs mt-0.5">
                    {b.due_date ? `Due ${fmtDate(b.due_date)}` : "No due date"}{b.recurring ? ` · ${b.frequency}` : ""}
                  </p>
                </div>
                <span className="text-slate-100 font-semibold shrink-0">${Number(b.amount).toFixed(2)}</span>
              </div>
              <button onClick={() => setHistoryId(b.id)} className="block w-full text-left" data-testid="bill-open-history"><BillProgress bill={b} /></button>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button variant="secondary" className="!py-2" onClick={() => { setContribFor(b); setAmount(""); }} data-testid="bill-contribute-btn"><Plus size={15} /> Add contribution</Button>
                <Button variant="ghost" className="!py-2" onClick={() => setHistoryId(b.id)} data-testid="bill-history-btn"><History size={15} /> History</Button>
                <div className="flex items-center gap-1 ml-auto">
                  <button onClick={() => markPaid(b)} data-testid="mark-paid-btn"
                    className={`text-xs px-2.5 py-1.5 rounded-lg font-medium ${b.status === "paid" ? "bg-emerald-500/15 text-emerald-400" : b.status === "overdue" ? "bg-red-500/10 text-red-400" : "bg-slate-800 text-slate-300"}`}>
                    {b.status === "paid" ? "Paid" : b.status === "overdue" ? "Overdue" : "Mark paid"}
                  </button>
                  <button onClick={() => startEdit(b)} className="p-1.5 text-slate-400 hover:text-sky-400" data-testid="edit-bill-btn"><Pencil size={14} /></button>
                  <button onClick={() => del(b)} className="p-1.5 text-slate-400 hover:text-red-400" data-testid="delete-bill-btn"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ContributionHistory open={!!historyBill} onClose={() => setHistoryId(null)} title={`${historyBill?.name || ""} history`}
        contributions={historyBill?.contributions} periods={historyBill?.history || []} onRemove={removeContribution} />

      <Modal open={!!contribFor} onClose={() => setContribFor(null)} title={`Contribute to ${contribFor?.name || ""}`} testId="bill-contribute-modal"
        footer={<><Button variant="secondary" full onClick={() => setContribFor(null)}>Cancel</Button><Button full loading={busy} onClick={contribute} data-testid="save-bill-contribution-btn">Add</Button></>}>
        {contribFor && contribFor.amount > 0 && (
          <p className="text-sm text-slate-400 mb-3">Remaining: <span className="text-sky-300 font-medium">${Math.max(0, contribFor.amount - (contribFor.paid_amount || 0)).toFixed(2)}</span></p>
        )}
        <Field label="Amount"><TextInput type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="20" autoFocus data-testid="bill-contribution-amount" /></Field>
      </Modal>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit bill" : "Add bill"} testId="bill-modal"
        footer={<><Button variant="secondary" full onClick={() => setOpen(false)}>Cancel</Button><Button full loading={busy} onClick={save} data-testid="save-bill-btn">Save</Button></>}>
        <Field label="Name"><TextInput value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Netflix" data-testid="bill-name" /></Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 [&>*]:min-w-0">
          <Field label="Amount"><TextInput type="number" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="22.99" data-testid="bill-amount" /></Field>
          <Field label="Due date"><DateInput value={form.due_date || ""} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} data-testid="bill-due-date" /></Field>
        </div>
        <Field label="Status">
          <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className="iks-input w-full min-w-0 px-3.5 py-2.5 text-sm">
            {STATUSES.map(([v, l]) => <option key={v} value={v} className="bg-slate-900">{l}</option>)}
          </select>
        </Field>
        <label className="flex items-center gap-2 text-sm text-slate-300 mb-3">
          <input type="checkbox" checked={!!form.recurring} onChange={(e) => setForm((f) => ({ ...f, recurring: e.target.checked }))} className="rounded border-white/20 bg-transparent text-blue-600" />
          Recurring
        </label>
        {form.recurring && (
          <Field label="Frequency">
            <select value={form.frequency} onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value }))} className="iks-input w-full px-3.5 py-2.5 text-sm">
              {["weekly", "monthly", "yearly"].map((v) => <option key={v} value={v} className="bg-slate-900">{v}</option>)}
            </select>
          </Field>
        )}
      </Modal>
    </div>
  );
}

function BillProgress({ bill }) {
  const paid = bill.paid_amount || 0;
  const total = Number(bill.amount) || 0;
  if (!paid && !bill.contributions?.length) return null;
  const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 100;
  const done = total > 0 && paid >= total;
  const byUser = (bill.contributions || []).reduce((a, c) => { a[c.user_name] = (a[c.user_name] || 0) + c.amount; return a; }, {});
  return (
    <div className="mt-3" data-testid="bill-progress">
      <div className="flex items-end justify-between mb-1.5 text-sm">
        <span className="text-slate-200 font-semibold">${paid.toFixed(2)} <span className="text-slate-500 font-normal">paid</span></span>
        <span className="text-slate-500 text-xs">{done ? "Fully covered" : `$${Math.max(0, total - paid).toFixed(2)} left`}</span>
      </div>
      <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${done ? "bg-emerald-500" : "bg-gradient-to-r from-blue-600 to-sky-400"}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-400">
        {Object.entries(byUser).map(([n, amt]) => (
          <span key={n}>{n}: <span className="text-sky-300">+${amt.toFixed(2)}</span></span>
        ))}
      </div>
    </div>
  );
}
